<template>
  <div class="tw-space-y-2">

    <!-- Section Header -->
    <div class="tw-flex tw-items-center tw-gap-2">
      <iconify-icon icon="solar:eye-bold-duotone" class="tw-text-indigo-500 tw-text-base" />
      <h2 class="tw-text-sm tw-font-semibold tw-text-gray-700">Views &amp; Engagement</h2>
      <div class="tw-flex-1 tw-h-px tw-bg-gray-200 tw-ml-1" />
    </div>

    <!-- Summary KPI Cards -->
    <div class="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-2">
      <div
        v-for="card in summaryCards"
        :key="card.label"
        class="tw-bg-white tw-rounded-lg tw-p-3 tw-border tw-border-solid tw-border-gray-200"
      >
        <p class="tw-text-[11px] tw-text-gray-500 tw-mb-1">{{ card.label }}</p>
        <p class="tw-text-lg tw-font-bold" :class="card.color">
          <span v-if="loading" class="tw-inline-block tw-w-10 tw-h-5 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
          <span v-else>{{ card.value.toLocaleString() }}</span>
        </p>
      </div>
    </div>

    <!-- Top Tables + Bookmark Summary -->
    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2">

      <!-- Top Viewed Products -->
      <TopViewedTable
        title="Top viewed products"
        :rows="data.top_viewed_products"
        :loading="loading"
        icon="solar:box-bold-duotone"
        icon-color="tw-text-green-500"
      />

      <!-- Top Viewed Companies -->
      <TopViewedTable
        title="Top viewed companies"
        :rows="data.top_viewed_companies"
        :loading="loading"
        icon="solar:buildings-2-bold-duotone"
        icon-color="tw-text-purple-500"
      />

      <!-- Top Viewed Profiles -->
      <TopViewedTable
        title="Top viewed profiles"
        :rows="data.top_viewed_users"
        :loading="loading"
        icon="solar:user-bold-duotone"
        icon-color="tw-text-blue-500"
      />
    </div>

    <!-- Bookmarks + Most Active Viewers + Views Trend -->
    <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-2">

      <!-- Top Bookmarked Products -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <iconify-icon icon="solar:bookmark-bold-duotone" class="tw-text-amber-500 tw-text-base" />
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Top bookmarked</h3>
        </div>

        <div class="tw-space-y-3">
          <div>
            <p class="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">Products</p>
            <BookmarkList :rows="data.top_bookmarked_products" :loading="loading" />
          </div>
          <div>
            <p class="tw-text-[10px] tw-font-medium tw-text-gray-400 tw-uppercase tw-tracking-wide tw-mb-1">Companies</p>
            <BookmarkList :rows="data.top_bookmarked_companies" :loading="loading" />
          </div>
        </div>

        <!-- Bookmark totals -->
        <div class="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-100 tw-grid tw-grid-cols-3 tw-gap-1">
          <div v-for="bm in bookmarkTotals" :key="bm.label" class="tw-text-center">
            <p class="tw-text-xs tw-font-semibold tw-text-gray-800">
              <span v-if="loading" class="tw-inline-block tw-w-6 tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
              <span v-else>{{ bm.value }}</span>
            </p>
            <p class="tw-text-[10px] tw-text-gray-400">{{ bm.label }}</p>
          </div>
        </div>
      </div>

      <!-- Most Active Viewers -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <iconify-icon icon="solar:eye-bold-duotone" class="tw-text-sky-500 tw-text-base" />
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Most active viewers</h3>
          <span class="tw-text-xs tw-text-gray-400 tw-ml-auto">last {{ days }}d</span>
        </div>

        <div v-if="loading" class="tw-space-y-2">
          <div v-for="i in 5" :key="i" class="tw-h-7 tw-bg-gray-100 tw-rounded tw-animate-pulse" />
        </div>

        <div v-else-if="!data.most_active_viewers?.length" class="tw-text-center tw-py-4 tw-text-gray-400 tw-text-xs">
          No data yet
        </div>

        <div v-else class="tw-space-y-1.5">
          <div
            v-for="(viewer, idx) in data.most_active_viewers"
            :key="viewer.id"
            class="tw-flex tw-items-center tw-justify-between tw-py-1 tw-border-b tw-border-gray-50 last:tw-border-b-0"
          >
            <div class="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
              <span class="tw-text-[10px] tw-font-semibold tw-text-gray-300 tw-w-4">#{{ idx + 1 }}</span>
              <div class="tw-min-w-0">
                <p class="tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">{{ viewer.name }}</p>
                <p class="tw-text-[10px] tw-text-gray-400 tw-truncate">{{ viewer.email }}</p>
              </div>
            </div>
            <span class="tw-text-xs tw-font-semibold tw-text-sky-600 tw-shrink-0 tw-ml-2">
              {{ viewer.total_views.toLocaleString() }}
            </span>
          </div>
        </div>
      </div>

      <!-- Views Trend Chart -->
      <div class="tw-bg-white tw-rounded-lg tw-p-4 tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex tw-items-center tw-gap-2 tw-mb-3">
          <iconify-icon icon="solar:graph-up-bold-duotone" class="tw-text-indigo-500 tw-text-base" />
          <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">Views trend</h3>
          <span class="tw-text-xs tw-text-gray-400 tw-ml-auto">last {{ days }}d</span>
        </div>

        <div v-if="loading" class="tw-h-40 tw-bg-gray-50 tw-rounded tw-animate-pulse" />

        <div v-else-if="!trendSeries[0]?.data?.length" class="tw-h-40 tw-flex tw-items-center tw-justify-center tw-text-gray-400 tw-text-xs">
          No data yet
        </div>

        <apexchart
          v-else
          type="area"
          height="170"
          :options="trendOptions"
          :series="trendSeries"
        />
      </div>
    </div>

    <!-- Period Selector (hidden — functionality kept for programmatic use) -->
    <div v-show="false" class="tw-flex tw-justify-end tw-gap-1">
      <button
        v-for="d in [7, 14, 30, 90]"
        :key="d"
        class="tw-text-xs tw-px-2.5 tw-py-1 tw-rounded tw-border tw-transition-colors"
        :class="days === d
          ? 'tw-bg-indigo-600 tw-text-white tw-border-indigo-600'
          : 'tw-bg-white tw-text-gray-500 tw-border-gray-200 hover:tw-border-indigo-300'"
        @click="changePeriod(d)"
      >
        {{ d }}d
      </button>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { admin } from '@/api/dashboard'

// Sub-components defined inline to keep the file self-contained
import TopViewedTable from './TopViewedTable.vue'
import BookmarkList from './BookmarkList.vue'

const days    = ref(30)
const loading = ref(true)
const data    = ref({
  summary:                  { products: {}, companies: {}, users: {} },
  top_viewed_products:      [],
  top_viewed_companies:     [],
  top_viewed_users:         [],
  top_bookmarked_products:  [],
  top_bookmarked_companies: [],
  views_trend:              [],
  most_active_viewers:      [],
  bookmark_summary:         { products: 0, companies: 0, users: 0 },
})

const summaryCards = computed(() => [
  { label: 'Product views',      value: data.value.summary.products?.total_views    ?? 0, color: 'tw-text-green-600' },
  { label: 'Product viewers',    value: data.value.summary.products?.unique_viewers ?? 0, color: 'tw-text-green-500' },
  { label: 'Company views',      value: data.value.summary.companies?.total_views   ?? 0, color: 'tw-text-purple-600' },
  { label: 'Company viewers',    value: data.value.summary.companies?.unique_viewers ?? 0, color: 'tw-text-purple-500' },
  { label: 'Profile views',      value: data.value.summary.users?.total_views       ?? 0, color: 'tw-text-blue-600' },
  { label: 'Profile viewers',    value: data.value.summary.users?.unique_viewers    ?? 0, color: 'tw-text-blue-500' },
])

const bookmarkTotals = computed(() => [
  { label: 'Products',  value: (data.value.bookmark_summary?.products  ?? 0).toLocaleString() },
  { label: 'Companies', value: (data.value.bookmark_summary?.companies ?? 0).toLocaleString() },
  { label: 'Users',     value: (data.value.bookmark_summary?.users     ?? 0).toLocaleString() },
])

// Chart
const trendSeries = computed(() => {
  const trend = data.value.views_trend ?? []
  return [
    { name: 'Products',  data: trend.map(d => d.products)  },
    { name: 'Companies', data: trend.map(d => d.companies) },
    { name: 'Profiles',  data: trend.map(d => d.users)     },
  ]
})

const trendOptions = computed(() => ({
  chart: { id: 'views-trend', toolbar: { show: false }, zoom: { enabled: false }, stacked: false },
  stroke: { curve: 'smooth', width: 2 },
  fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
  dataLabels: { enabled: false },
  legend: { position: 'top', fontSize: '10px', offsetY: -4 },
  xaxis: {
    categories: (data.value.views_trend ?? []).map(d => d.date),
    labels: { style: { fontSize: '9px' }, rotate: -30 },
    axisBorder: { show: false },
    axisTicks: { show: false },
    tickAmount: 7,
  },
  yaxis: { labels: { style: { fontSize: '10px' } }, min: 0, forceNiceScale: true },
  grid: { strokeDashArray: 4, borderColor: '#e5e7eb' },
  colors: ['#16a34a', '#7c3aed', '#2563eb'],
  tooltip: { theme: 'light', x: { show: true } },
}))

async function load() {
  loading.value = true
  try {
    const response = await admin.viewAnalytics({ days: days.value })
    data.value = response.data
  } finally {
    loading.value = false
  }
}

function changePeriod(d) {
  days.value = d
}

watch(days, load)
onMounted(load)
</script>
